import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, ArrowRight } from "lucide-react";
import Card from "../components/ui/Card";
import PrimaryButton from "../components/ui/PrimaryButton";
import { fetchHistory, ScanHistoryItem } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "./DashboardPage.css";

type DashboardPageProps = {
  onStartScan?: () => void;
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(parseInt(timestamp) * 1000);
  return date.toLocaleString();
};

const getRiskColor = (riskScore: string): string => {
  const score = parseFloat(riskScore);
  if (score >= 70) return "#FF3B30";
  if (score >= 50) return "#FF9500";
  if (score >= 30) return "#FFCC00";
  return "#34C759";
};

const getRiskLabelTone = (label: string): string => {
  const normalized = label.toLowerCase();
  if (normalized.includes("high")) return "#ef4444";
  if (normalized.includes("moderate") || normalized.includes("medium")) return "#f97316";
  return "#16a34a";
};

const DashboardPage: React.FC<DashboardPageProps> = ({ onStartScan }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [history, setHistory] = useState<ScanHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Re-fetch history when token changes (e.g., after login/logout)
  useEffect(() => {
    const loadHistory = async () => {
      setIsLoading(true);
      const data = await fetchHistory(token || undefined);
      setHistory(data);
      setIsLoading(false);
    };
    loadHistory();
  }, [token]);

  const latestScan = history[0] ?? null;
  const averageRisk = history.length
    ? history.reduce((total, entry) => total + parseFloat(entry.riskScore ?? "0"), 0) /
      history.length
    : null;
  const highRiskCount = history.filter((entry) =>
    entry.riskLevel.toLowerCase().includes("high")
  ).length;

  const lastScanDate = latestScan
    ? new Date(parseInt(latestScan.timestamp) * 1000)
    : null;
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const nextCheckDate = lastScanDate
    ? new Date(lastScanDate.getTime() + 30 * MS_PER_DAY)
    : new Date(Date.now() + 7 * MS_PER_DAY);
  const daysUntilNext = Math.max(
    0,
    Math.ceil((nextCheckDate.getTime() - Date.now()) / MS_PER_DAY)
  );
  const nextCheckLabel = nextCheckDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const sinceLastScanDays = lastScanDate
    ? Math.max(0, Math.ceil((Date.now() - lastScanDate.getTime()) / MS_PER_DAY))
    : null;
  const lastScanLabel = lastScanDate
    ? lastScanDate.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  const lastScanDetail = latestScan ? formatTimestamp(latestScan.timestamp) : null;

  const averageRiskLabel = averageRisk !== null ? `${averageRisk.toFixed(1)}%` : "—";
  const nextCheckDistanceLabel = daysUntilNext === 0
    ? "Today"
    : `${daysUntilNext} day${daysUntilNext === 1 ? "" : "s"} away`;
  const sinceLastScanLabel = sinceLastScanDays !== null
    ? sinceLastScanDays === 0
      ? "Completed today"
      : `${sinceLastScanDays} day${sinceLastScanDays === 1 ? "" : "s"} ago`
    : "Not yet scanned";

  const nextCheckNote = latestScan
    ? `Based on your last scan captured ${lastScanDetail}.`
    : "Complete your first scan to personalise reminders.";

  return (
    <div className="dashboard">
      <section className="dashboard__card-grid">
        <Card className="dashboard__scan-card" padding="lg" elevated>
          <header className="dashboard__card-header">
            <div>
              <h2>Start Scan</h2>
              <p>Kick off a new lesion scan with one tap and receive guidance instantly.</p>
            </div>
          </header>
          <div className="dashboard__guide">
            <h3>Quick tips</h3>
            <ul>
              <li>Scan high-risk areas regularly (face, scalp, shoulders, calves).</li>
              <li>Use even lighting and fill the frame with the lesion.</li>
              <li>Repeat monthly to build a personal skin history.</li>
            </ul>
          </div>
          <PrimaryButton
            onClick={() => {
              onStartScan?.();
              navigate("/scan");
            }}
            icon={<Camera size={18} strokeWidth={2.2} />}
            style={{ marginTop: 24, alignSelf: "flex-start" }}
          >
            Start a scan
          </PrimaryButton>
        </Card>

        <div className="dashboard__grid-split">
          <Card className="dashboard__insight-card" padding="md">
            <h3>Next recommended check</h3>
            <div className="dashboard__insight-meta">
              {nextCheckLabel}
              <span>{history.length ? nextCheckDistanceLabel : "Plan your first scan"}</span>
            </div>
            <p className="dashboard__insight-note">{nextCheckNote}</p>
            <span className="dashboard__chip">30-day cadence</span>
          </Card>

          <Card className="dashboard__insight-card" padding="md">
            <h3>Your scan steps</h3>
            <ol className="dashboard__step-list">
              <li>Photograph the lesion in balanced lighting.</li>
              <li>Review AI guidance & note visible changes.</li>
              <li>Create a trend by scanning again next month.</li>
            </ol>
          </Card>

          <Card className="dashboard__insight-card" padding="md">
            <h3>Quick actions</h3>
            <div className="dashboard__quick-links">
              <button type="button" onClick={() => navigate("/results")}>
                Review latest results
                <ArrowRight size={16} strokeWidth={2.2} />
              </button>
              <button type="button" onClick={() => navigate("/uv-index")}>
                Check UV safety today
                <ArrowRight size={16} strokeWidth={2.2} />
              </button>
              <button type="button" onClick={() => navigate("/profile")}>
                Manage health profile
                <ArrowRight size={16} strokeWidth={2.2} />
              </button>
            </div>
          </Card>
        </div>
      </section>

      <section className="dashboard__metrics">
        <Card className="dashboard__insight-card" padding="md">
          <h3>Average risk score</h3>
          <div className="dashboard__insight-meta">
            {averageRiskLabel}
            <span>
              {history.length
                ? `Across ${history.length} scan${history.length === 1 ? "" : "s"}`
                : "Awaiting first scan"}
            </span>
          </div>
          <p className="dashboard__insight-note">
            Keep a consistent cadence to spot trends early and escalate if scores rise.
          </p>
        </Card>

        <Card className="dashboard__insight-card" padding="md">
          <h3>High-risk scans</h3>
          <div className="dashboard__insight-meta">
            {history.length ? highRiskCount : "—"}
            <span>{history.length ? "flagged to date" : "No data yet"}</span>
          </div>
          <p className="dashboard__insight-note">
            {highRiskCount > 0
              ? "Schedule a dermatologist review for flagged scans."
              : "No high-risk scans have been flagged so far."}
          </p>
        </Card>

        <Card className="dashboard__insight-card" padding="md">
          <h3>Last scan</h3>
          <div className="dashboard__insight-meta">
            {lastScanLabel ?? "—"}
            <span>{sinceLastScanLabel}</span>
          </div>
          <p className="dashboard__insight-note">
            {lastScanDetail
              ? `Captured ${lastScanDetail}.`
              : "Complete a scan to unlock personalised follow-up guidance."}
          </p>
        </Card>
      </section>

      <section className="dashboard__history">
        <header className="dashboard__history-header">
          <h3>Previous results</h3>
        </header>
        <div className="dashboard__history-list">
          {isLoading ? (
            <Card className="dashboard__history-item dashboard__history-item--loading">
              <p>Loading scan history...</p>
            </Card>
          ) : history.length === 0 ? (
            <Card className="dashboard__history-item dashboard__history-item--empty">
              <p>No previous scans found.</p>
              <p className="dashboard__history-hint">Complete your first scan to see results here.</p>
            </Card>
          ) : (
            history.map((scan, index) => (
              <Card className="dashboard__history-item" key={scan.timestamp}>
                <div className="dashboard__history-row">
                  <div className="dashboard__history-info">
                    <h4>Scan #{scan.scanNumber}</h4>
                    <span className="dashboard__history-date">
                      Captured: {formatTimestamp(scan.timestamp)}
                    </span>
                  </div>
                  <div 
                    className="dashboard__history-score"
                    style={{ backgroundColor: getRiskColor(scan.riskScore) }}
                  >
                    {parseFloat(scan.riskScore).toFixed(1)}%
                  </div>
                </div>
                <div
                  className="dashboard__history-level"
                  style={{ color: getRiskLabelTone(scan.riskLevel) }}
                >
                  {scan.riskLevel}
                </div>
              </Card>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
